import React, { useState } from 'react';
import { ChevronDown, ChevronUp, PauseCircle, Moon, Heart, Flame, Users, BookOpen, Clock, Cloud } from 'lucide-react';
import { SlowDownRule } from '../types';

const RULES: SlowDownRule[] = [
  { id: 1, title: "The World Moves Fast", chapter: "Rest", description: "When our minds move fast, the world moves fast. When our minds slow down, the world slows down. The world is a reflection of your state of mind.", icon: "PauseCircle" },
  { id: 2, title: "Befriend Your Emotions", chapter: "Mindfulness", description: "Observe your negative emotions like a passing cloud. Do not fight them, do not feed them. Just watch them acknowledge them, and let them pass.", icon: "Cloud" },
  { id: 3, title: "Temper Your Passion", chapter: "Passion", description: "Passion is good, but if it is too hot, it burns you and others. Moderate your enthusiasm so it can last a long time, like a gentle, steady fire.", icon: "Flame" },
  { id: 4, title: "You Are Right", chapter: "Relationships", description: "In a relationship, being right is less important than being together. Do you want to win the argument, or do you want to keep the connection? Lower your guard.", icon: "Users" },
  { id: 5, title: "Love Yourself First", chapter: "Love", description: "If you do not love yourself, you cannot love others. Treat yourself with the same kindness and generosity you would offer a guest.", icon: "Heart" },
  { id: 6, title: "Life is a School", chapter: "Life", description: "Everyone you meet is a teacher. Even the ones you dislike teach you patience, or show you what you don't want to become.", icon: "BookOpen" },
  { id: 7, title: "Trust the Timing", chapter: "The Future", description: "Do not rush. Things happen according to their own time. You cannot force the spring to come, but it always does.", icon: "Clock" },
  { id: 8, title: "Find Your Silence", chapter: "Spirituality", description: "Silence is not the absence of sound, but the presence of awareness. In silence, you find your true self.", icon: "Moon" }
];

const IconMap: Record<string, React.ReactNode> = {
  PauseCircle: <PauseCircle size={24} />,
  Cloud: <Cloud size={24} />,
  Flame: <Flame size={24} />,
  Users: <Users size={24} />,
  Heart: <Heart size={24} />,
  BookOpen: <BookOpen size={24} />,
  Clock: <Clock size={24} />,
  Moon: <Moon size={24} />,
};

const SlowDown: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-purple-50 text-purple-600 rounded-full mb-4">
          <PauseCircle size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">Things You Can See Only When You Slow Down</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"Is it the world that's busy, or is it my mind?"</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          Inspired by Haemin Sunim, this guide helps you find stillness in a chaotic world. 
          When you slow down, you begin to see things you missed before.
        </p>
      </header>

      <div className="space-y-4">
        {RULES.map((rule) => (
          <div 
            key={rule.id} 
            className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-purple-400 shadow-md' : 'border-nunchi-200 hover:border-purple-300'}`}
          >
            <button 
              onClick={() => toggle(rule.id)}
              className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-purple-100 text-purple-600' : 'bg-gray-50 text-gray-400'}`}>
                  {IconMap[rule.icon]}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                    {rule.title}
                  </h3>
                  <p className="text-xs text-nunchi-500 font-serif uppercase tracking-wider">{rule.chapter}</p>
                </div>
              </div>
              <div className="text-nunchi-400">
                {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>
            
            <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
              <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-purple-200 mt-2">
                {rule.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SlowDown;